import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { SettingsIcon } from "lucide-react";
import { z } from "zod";

import { Column } from "@/components/Column";
import { KeyboardShortcut } from "@/components/KeyboardShortcut";
import { RichTextDisplay } from "@/components/RichTextDisplay";
import { Row } from "@/components/Row";
import { HumanOverrideBadge } from "@jobs/pipeline/shared/AgenticScoreChip";
import { FormRichTextInput } from "@/components/form/FormRichTextInput";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Label } from "@/components/ui/label";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { AGENTIC_SCORE_OPTIONS } from "@/lib/agenticScore";
import {
  agenticScoreBadgeClassNames,
  agenticScoreBucketConfig,
  agenticScoreIconClassNames,
} from "@/lib/constants";
import { tiptap } from "@/lib/tiptap";
import { cn } from "@/lib/utils";
import { AgenticScoreBucket } from "@/types";

const feedbackSchema = z.object({
  bucket: z.nativeEnum(AgenticScoreBucket),
  justification: z.string(),
});

type FeedbackFormValues = z.infer<typeof feedbackSchema>;

// Fixed-spec agentic rating pill shared by the candidacy sheet and the
// application review portal. Matches Corin's design exactly (size, font,
// icon, colors) — read-only unless `onSubmit` is given, in which case
// clicking opens the bucket-picker + justification popover.
export const AgenticScorePill = ({
  bucket,
  isHumanRated = false,
  isLoading = false,
  justification,
  onSubmit,
}: {
  bucket: AgenticScoreBucket | null;
  isHumanRated?: boolean;
  isLoading?: boolean;
  justification?: string | null;
  onSubmit?: (bucket: AgenticScoreBucket, justification: string) => void;
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const hasJustificationTooltip = !!justification;

  const render = () => {
    if (isLoading) {
      return (
        <Badge
          className={cn(
            "border-transparent bg-slate-100 font-semibold text-slate-600",
            agenticScoreBadgeClassNames.xs,
          )}
        >
          <SettingsIcon className="h-3.5 w-3.5 animate-spin" />
          Rating
        </Badge>
      );
    }

    if (!bucket) return null;

    const { Icon, label, className } = agenticScoreBucketConfig[bucket];

    const pill = (
      <Badge
        className={cn(
          "relative border-transparent font-semibold",
          agenticScoreBadgeClassNames.xs,
          className,
        )}
      >
        {isHumanRated && (
          <HumanOverrideBadge className={className} variant="xs" />
        )}
        <Icon className={agenticScoreIconClassNames.xs} />
        {label}
      </Badge>
    );

    if (!onSubmit) {
      if (!hasJustificationTooltip) return pill;
      return (
        <TooltipProvider delayDuration={300}>
          <Tooltip>
            <TooltipTrigger asChild>
              <span>{pill}</span>
            </TooltipTrigger>
            <TooltipContent className="max-w-80" side="bottom">
              <RichTextDisplay richText={justification} />
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    return (
      <Popover onOpenChange={setIsOpen} open={isOpen}>
        <TooltipProvider delayDuration={300}>
          <Tooltip>
            <TooltipTrigger asChild>
              <PopoverTrigger asChild>
                <button
                  className="inline-flex cursor-pointer border-0 bg-transparent p-0"
                  type="button"
                >
                  {pill}
                </button>
              </PopoverTrigger>
            </TooltipTrigger>
            {hasJustificationTooltip && (
              <TooltipContent className="max-w-80" side="bottom">
                <RichTextDisplay richText={justification} />
              </TooltipContent>
            )}
          </Tooltip>
        </TooltipProvider>
        <AgenticScorePillFeedback
          bucket={bucket}
          isOpen={isOpen}
          onSubmit={onSubmit}
          setIsOpen={setIsOpen}
        />
      </Popover>
    );
  };

  return render();
};

const AgenticScorePillFeedback = ({
  bucket,
  isOpen,
  onSubmit,
  setIsOpen,
}: {
  bucket: AgenticScoreBucket;
  isOpen: boolean;
  onSubmit: (bucket: AgenticScoreBucket, justification: string) => void;
  setIsOpen: (open: boolean) => void;
}) => {
  const form = useForm<FeedbackFormValues>({
    resolver: zodResolver(feedbackSchema),
    defaultValues: { bucket, justification: "" },
  });

  useEffect(() => {
    if (isOpen) {
      form.reset({ bucket, justification: "" });
    }
  }, [bucket, form, isOpen]);

  const justification = form.watch("justification");
  const selectedBucket = form.watch("bucket");
  const canSubmit = !tiptap.isEmptyFn(justification);

  const handleSubmit = (data: FeedbackFormValues) => {
    if (tiptap.isEmptyFn(data.justification)) return;
    onSubmit(data.bucket, data.justification);
    setIsOpen(false);
    form.reset();
  };

  return (
    <PopoverContent align="start" className="w-[420px] px-3 py-4">
      <Form {...form}>
        <form
          className="flex flex-col gap-3 text-sm"
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onSubmit={form.handleSubmit(handleSubmit)}
        >
          <Column className="gap-2">
            <Label className="text-sm font-semibold text-slate-900">
              Agentic rating
            </Label>
            <Row className="flex-nowrap gap-2">
              {AGENTIC_SCORE_OPTIONS.map((option) => {
                const optionConfig = agenticScoreBucketConfig[option];
                const OptionIcon = optionConfig.Icon;
                const isSelected = selectedBucket === option;
                return (
                  <Button
                    className={cn(
                      "h-auto gap-1.5 rounded-full border-0 px-3 py-1 text-sm font-semibold shadow-none",
                      optionConfig.className,
                      optionConfig.hoverClassName,
                      isSelected && "ring-2 ring-primary-500 ring-offset-1",
                    )}
                    key={option}
                    onClick={() => {
                      form.setValue("bucket", option, { shouldDirty: true });
                    }}
                    type="button"
                  >
                    <OptionIcon className="h-4 w-4 shrink-0" />
                    {optionConfig.label}
                  </Button>
                );
              })}
            </Row>
          </Column>
          <FormRichTextInput
            editorHeight="96px"
            fieldName="justification"
            form={form}
            label={
              <>
                Justification{" "}
                <span className="font-normal text-slate-500">(required)</span>
              </>
            }
          />
          <Row className="justify-end pt-1">
            <Button
              className="gap-1.5 rounded-full"
              disabled={!canSubmit}
              type="submit"
            >
              Submit
              <KeyboardShortcut text="⌘↵" variant="primary" />
            </Button>
          </Row>
        </form>
      </Form>
    </PopoverContent>
  );
};
